import { useEffect, useRef } from 'react';
import { useState } from 'react';
import {
  buildThread, describeEvent, formatAmount, formatIstLongDate, formatIstTime, istDayKey,
  isWindowOpen, parseOrder, renderWhatsAppText, windowRemaining,
  type Agent, type Conversation, type ConversationEvent, type Message, type Note,
  type OrderPayload, type ScheduledMessage, type ThreadEntry,
} from '@wa/shared';
import { Avatar } from '@/components/Avatar';
import { Ticks } from '@/components/Ticks';
import { MediaBubble } from '@/components/MediaBubble';
import { Composer } from '@/components/Composer';
import { NoteComposer } from '@/components/NoteComposer';
import { NoteBubble } from '@/components/NoteBubble';
import { ReactionBar } from '@/components/ReactionBar';
import { SchedulePanel } from '@/components/SchedulePanel';
import { ChatMenu } from '@/inbox/ChatMenu';
import {
  AssigneePill, CallButton, DetailsToggle, DoneButton, IconButton,
} from '@/inbox/ChatHeaderActions';
import { SessionOpener } from '@/inbox/SessionOpener';

interface Props {
  conversation: Conversation;
  messages: Message[];
  notes: Note[];
  /** The system pills. Empty is normal — most conversations have no events. */
  events: ConversationEvent[];
  agents: Agent[];
  isLoading: boolean;
  isSending: boolean;
  sendError: string | null;
  onSend: (body: string) => Promise<unknown> | void;
  onSendMedia: (file: File, caption: string) => Promise<unknown> | void;
  onSendStoredMedia?: (mediaAssetId: number, caption: string) => Promise<unknown> | void;
  isUploading: boolean;
  onOpenTemplates: () => void;
  onOpenProducts: () => void;
  onAddNote: (body: string, mentionedAgentIds: number[]) => void;
  onDeleteNote: (id: number) => void;
  isSavingNote: boolean;
  /** Whose reactions count as "mine". Null for an API key, which cannot react. */
  myAgentId: number | null;
  onReact: (messageId: number, emoji: string | null) => void;
  isReacting: boolean;
  scheduled: ScheduledMessage[];
  onSchedule: (body: string, whenUtcIso: string) => void;
  onCancelScheduled: (id: number) => void;
  onBack?: () => void;
  onOpenDetails?: () => void;
  onResolve: () => void;
  isSaving: boolean;
  onAssign: (agentId: number | null) => void;
  /** Search inside this thread. Empty means "not searching". */
  search: string;
  onSearch: (value: string) => void;
  /** Absent where the details panel is a sheet rather than a column. */
  onToggleDetails?: () => void;
  detailsOpen: boolean;
  /** The closed-window composer sends one, with its single variable filled. */
  onSendTemplate: (templateId: number, variables: string[]) => void;
  /** Puts a resolved conversation back to open. The composer is gone until it is. */
  onReopen: () => void;
  /** Any status, for the kebab's Pending. Resolve and reopen have their own props
      because they are one-click controls on the header, not menu entries. */
  onStatus: (status: number) => void;
}

export function MessageThread({
  conversation, messages, notes, events, agents, isLoading, isSending, sendError, onSend, onSendMedia, onSendStoredMedia,
  isUploading, onOpenTemplates, onOpenProducts, onAddNote, onDeleteNote, isSavingNote,
  myAgentId, onReact, isReacting, scheduled, onSchedule, onCancelScheduled,
  onBack, onOpenDetails, onResolve, isSaving, onSendTemplate, onReopen, onStatus,
  onAssign, search, onSearch, onToggleDetails, detailsOpen,
}: Props) {
  const [mode, setMode] = useState<'reply' | 'note'>('reply');
  const [searching, setSearching] = useState(search.length > 0);
  const thread = buildThread(messages, notes, events);
  const bottom = useRef<HTMLDivElement>(null);
  const scroller = useRef<HTMLDivElement>(null);
  const open = isWindowOpen(conversation);
  const resolved = conversation.status === 2;

  /*
   * The newest message's id, NOT how many there are.
   *
   * This effect used to depend on `messages.length`, and that worked right up until
   * a conversation got busy. The thread endpoint returns `TOP 40` — the newest page,
   * never accumulated — so on any conversation with 40 or more messages a new arrival
   * pushes the oldest one off the page and the length comes back **40 again**. React
   * sees no change, the effect never runs, and the agent sits there watching a thread
   * that has silently stopped following the conversation. Under 40 messages it is
   * perfect, which is why it shipped: every conversation anybody tests with is new.
   *
   * An id changes whether or not the count does.
   */
  const newestId = messages.length ? Math.max(...messages.map((m) => m.id)) : 0;

  /*
   * Following, or reading history — the thread must never do both at once.
   *
   * Jumping to the bottom on every arrival is the other half of this bug and the
   * more annoying one: an agent scrolled up to check what was promised last week
   * gets yanked away mid-sentence by an unrelated message. So the thread follows
   * only while it is already at the bottom, and otherwise counts what it is holding
   * back and offers a way down. Same rule WhatsApp itself uses.
   */
  const [following, setFollowing] = useState(true);
  const [held, setHeld] = useState(0);

  // The newest message the agent has actually had in front of them. Held is counted
  // against this rather than incremented per render: two messages arriving in one
  // refetch are two messages, and a pill that says "1 new message" above two of them
  // is worse than no pill.
  const lastSeen = useRef(0);

  const atBottom = () => {
    const el = scroller.current;
    // 48px, not 0: a half-pixel from a zoomed browser or a rounded row height must
    // not read as "the agent has scrolled away".
    return !el || el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };

  const catchUp = () => { lastSeen.current = newestId; setFollowing(true); setHeld(0); };

  const jumpToBottom = () => {
    bottom.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
    catchUp();
  };

  // Opening a conversation always lands at the newest message, whatever the
  // previous one was doing.
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: 'end' });
    catchUp();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id]);

  useEffect(() => {
    if (!newestId) return;
    if (following || atBottom()) {
      bottom.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
      catchUp();
    } else {
      setHeld(messages.filter((m) => m.id > lastSeen.current).length);
    }
    // `following` is deliberately not a dependency: this fires on new content, not
    // on the agent scrolling. Its current value is read, never watched.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newestId]);

  return (
    <section className="flex h-full min-h-0 flex-col">
      {/* Header stays; only the middle scrolls. */}
      <header className="flex shrink-0 items-center gap-2.5 border-b border-(--color-line) bg-(--color-surface) px-3 py-2.5">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="tap grid shrink-0 place-items-center rounded px-1 text-(--color-muted) hover:text-(--color-ink)"
            aria-label="Back to conversations"
          >
            <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true">
              <path d="M12.5 4 6.5 10l6 6" fill="none" stroke="currentColor" strokeWidth="1.7"
                    strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        <Avatar name={conversation.displayName} waId={conversation.waId} size={36} />

        {/* One line, not two.
            When a contact has no name we were printing the number as the title and
            the same number again underneath it — the design being matched shows the
            name if there is one and the number if there is not, once. The number
            lives in the details panel, which is where somebody goes to copy it. */}
        <button
          type="button"
          onClick={onOpenDetails}
          disabled={!onOpenDetails}
          className="min-w-0 flex-1 truncate text-left text-body font-semibold disabled:cursor-default"
        >
          {conversation.displayName ?? `+${conversation.waId}`}
        </button>

        {/* Only while the window is open, and then it is a countdown rather than a
            label. The reference has no chip here at all; a closed window is said by
            the composer, loudly, and repeating it in the header is noise. How long
            is left is not something the reference shows and an agent needs it. */}
        {open && (
          <span
            className="hidden shrink-0 items-center gap-1.5 rounded-(--radius-chip) bg-(--color-brand-wash) px-2.5 py-1 text-micro font-medium text-(--color-good) sm:flex"
            title="Free-form replies allowed until then"
          >
            <span aria-hidden="true" className="size-1.5 rounded-full bg-(--color-good)" />
            {windowRemaining(conversation)} left
          </span>
        )}

        {/* Call, assignee, done, search, kebab, details — the order the design
            being matched uses. Icon-only, every one with a tooltip and an
            accessible name, because six unlabelled glyphs is a memory test. */}
        <CallButton />

        <AssigneePill
          conversation={conversation}
          agents={agents}
          isSaving={isSaving}
          onAssign={onAssign}
        />

        <DoneButton
          done={conversation.status === 2}
          disabled={isSaving}
          onClick={onResolve}
        />

        <IconButton
          label={searching ? 'Close search' : 'Search this chat'}
          active={searching}
          onClick={() => { setSearching((v) => !v); onSearch(''); }}
        >
          <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
            <circle cx="9" cy="9" r="5.4" fill="none" stroke="currentColor" strokeWidth="1.7" />
            <path d="M13.2 13.2 17 17" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
        </IconButton>

        <ChatMenu conversation={conversation} onStatus={onStatus} />

        {onToggleDetails && <DetailsToggle open={detailsOpen} onClick={onToggleDetails} />}
      </header>

      {searching && (
        <div className="shrink-0 border-b border-(--color-line) bg-(--color-surface) px-3 py-2">
          <input
            autoFocus
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search in this chat"
            aria-label="Search in this chat"
            className="h-9 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-sunken) px-2.5 text-body outline-none focus:border-(--color-brand) focus:bg-(--color-surface)"
          />
          {search.trim().length === 1 && (
            <p className="mt-1 text-micro text-(--color-faint)">Type one more character.</p>
          )}
        </div>
      )}

      <div
        ref={scroller}
        onScroll={() => {
          const here = atBottom();
          setFollowing(here);
          if (here) catchUp();
        }}
        className="chat-ground pane-scroll relative min-h-0 flex-1 px-3 py-4"
      >
        {isLoading && messages.length === 0 && (
          <p className="text-center text-body text-(--color-muted)">Loading…</p>
        )}

        <div className="flex flex-col gap-1.5">
          {thread.map((entry, index) => {
            const previous = thread[index - 1];
            // One helper rather than a ternary per branch. It was a ternary, and
            // adding a third kind of entry turned it into a type error in two
            // places — which is the compiler catching exactly the bug a third
            // branch would otherwise have introduced silently.
            const at = stampOf(entry);
            const previousAt = previous ? stampOf(previous) : null;
            const showDay = !previousAt || !sameDay(previousAt, at);

            if (entry.kind === 'event') {
              return (
                <div key={`event-${entry.event.id}`} className="contents" data-thread-item="event">
                  {showDay && <DayPill at={at} />}

                  {/* Centred, white, small. It is context rather than content, and
                      the whole design of it is to be readable when looked for and
                      ignorable when not. */}
                  <div className="my-1 flex justify-center">
                    <span className="group/evt inline-flex max-w-[80%] items-center rounded-(--radius-chip) bg-(--color-surface) px-2.5 py-1 text-micro text-(--color-muted) shadow-sm">
                      <span className="min-w-0 text-center">{describeEvent(entry.event)}</span>
                      {/* The time slides out on hover. A width cannot transition to
                          `auto`, so this is the same 0fr → 1fr grid track the filter
                          accordions use — the one mechanism in this codebase that
                          animates to a size nobody has measured.

                          It stays in the DOM at zero width rather than being
                          conditionally rendered, so a screen reader reads the time
                          on every event whether or not a pointer ever arrives. And
                          on a touch screen there is no hover to give, so it is
                          simply always open there rather than permanently hidden. */}
                      <span
                        className="grid grid-cols-[0fr] transition-[grid-template-columns] duration-200 ease-out group-hover/evt:grid-cols-[1fr] [@media(hover:none)]:grid-cols-[1fr] motion-reduce:transition-none"
                      >
                        <span className="overflow-hidden">
                          <span className="pl-2 whitespace-nowrap tabular-nums">
                            {formatIstTime(at)}
                          </span>
                        </span>
                      </span>
                    </span>
                  </div>
                </div>
              );
            }

            if (entry.kind === 'note') {
              return (
                <div key={`note-${entry.note.id}`} className="contents" data-thread-item="note">
                  {showDay && <DayPill at={at} />}
                  <NoteBubble
                    note={entry.note}
                    agents={agents}
                    onDelete={() => onDeleteNote(entry.note.id)}
                  />
                </div>
              );
            }

            const message = entry.message;
            const outbound = message.direction === 1;
            const failed = message.status === 4;

            return (
              <div key={message.id} className="contents" data-thread-item="message">
                {showDay && <DayPill at={message.createdAt} />}

                <div className={outbound ? 'flex flex-col items-end' : 'flex flex-col items-start'}>
                  <div
                    className={[
                      'max-w-[min(34rem,85%)] rounded-lg px-2.5 py-1.5 text-body leading-snug shadow-sm',
                      failed
                        ? 'border border-(--color-bad) bg-(--color-surface)'
                        : outbound
                          ? 'bg-(--color-bubble-out) text-(--color-bubble-ink)'
                          : 'bg-(--color-bubble-in) text-(--color-bubble-ink)',
                    ].join(' ')}
                  >
                    {/* Only on the first bubble of a run by the same person. Repeating
                        it above every bubble triples the lines in a back-and-forth
                        and is the loudest thing in a quiet thread. */}
                    {outbound && message.sentByAgentName && (() => {
                      const prev = thread[index - 1];
                      const samePerson =
                        prev?.kind === 'message'
                        && prev.message.direction === 1
                        && prev.message.sentByAgentName === message.sentByAgentName;
                      return samePerson ? null : (
                        <p className="mb-0.5 text-micro font-semibold text-(--color-bubble-sender)">
                          {message.sentByAgentName}
                        </p>
                      );
                    })()}

                    {/* Deleted from the owner's phone, on a coexistence number.
                        First, and instead of everything else: the body is already
                        null server-side, so without this branch the bubble is
                        blank apart from a timestamp — which reads as a message
                        that failed to load rather than one that was withdrawn. */}
                    {message.revokedAt ? (
                      <p className="flex items-center gap-1.5 italic opacity-70">
                        <svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true" className="shrink-0">
                          <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                          <path d="M5.4 5.4l9.2 9.2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                        </svg>
                        This message was deleted
                      </p>
                    ) : (
                      <>
                        {message.mediaAssetId !== null && (
                          <div className="mb-1"><MediaBubble message={message} /></div>
                        )}

                        {/* An order: the basket as a card, not a line of text. The
                            items were named at arrival, so a product renamed since
                            still reads as what was bought. */}
                        {message.messageType === 'order' && parseOrder(message.orderPayload) && (
                          <OrderCard order={parseOrder(message.orderPayload)!} />
                        )}

                        {/* break-words is what stops a pasted URL from widening the bubble
                            past the column and pushing the layout sideways. */}
                        {message.messageType === 'order' && parseOrder(message.orderPayload) ? null : message.body ? (
                          <p className="break-words whitespace-pre-wrap">{renderWhatsAppText(message.body)}</p>
                        ) : message.messageType === 'media_placeholder' ? (
                          /* Media from before this number was connected, which Meta
                             will not transfer: only attachments sent within a
                             fortnight of onboarding come across, and the rest arrive
                             as a placeholder with no bytes behind it and no way to
                             fetch them. Said plainly, because "[media_placeholder]"
                             reads as something broken and this is not broken. */
                          <p className="flex items-center gap-1.5 opacity-70">
                            <svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true" className="shrink-0">
                              <path d="M12.8 7.2L8.2 11.8a2 2 0 002.8 2.8l4.6-4.6a3.4 3.4 0 00-4.8-4.8L5.6 10.4a4.8 4.8 0 006.8 6.8"
                                    fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            <em>Attachment — not transferred from the phone</em>
                          </p>
                        ) : message.mediaAssetId === null ? (
                          <p className="opacity-70"><em>[{message.messageType}]</em></p>
                        ) : null}
                      </>
                    )}

                    <p className={[
                      'mt-0.5 flex items-center justify-end gap-1 text-micro',
                      outbound ? 'text-(--color-bubble-stamp)' : 'text-(--color-muted)',
                    ].join(' ')}>
                      {/* The customer saw different words first. Without this the
                          thread quietly shows the corrected text as though it were
                          what was sent, and an agent reading back a conversation
                          cannot tell why the customer is answering something else. */}
                      {message.editedAt && !message.revokedAt && (
                        <span className="italic opacity-70" title={`Edited on the phone at ${formatIstTime(message.editedAt)}`}>
                          edited
                        </span>
                      )}
                      <span className="tabular-nums">
                        {formatIstTime(message.createdAt)}
                      </span>
                      {outbound && <Ticks status={message.status} />}
                    </p>

                    {failed && message.errorTitle && (
                      <p className="mt-1.5 flex items-start gap-1.5 rounded-(--radius-field) bg-(--color-bad)/8 px-2 py-1 text-micro text-(--color-bad)">
                        <svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true" className="mt-px shrink-0">
                          <circle cx="10" cy="10" r="7.2" fill="none" stroke="currentColor" strokeWidth="1.6" />
                          <path d="M10 6.4v4.2M10 13.4v.1" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                        </svg>
                        {/* The sentence, not the code. Meta repeats the number inside
                            its own message — "131058: (#131058) Hello World templates
                            can only be sent from…" — and neither copy helps the person
                            reading it. The code stays in the title for a support call. */}
                        <span className="min-w-0" title={`WhatsApp error ${message.errorCode}`}>
                          {message.errorTitle.replace(/^\(#\d+\)\s*/, '')}
                        </span>
                      </p>
                    )}
                  </div>

                  {/* Below the bubble rather than overlapping it. An overlapping
                      chip has to be positioned against a bubble whose height is
                      unknown until the text wraps, and it covers the timestamp on
                      a narrow screen. A message that never reached WhatsApp has no
                      id there to react to, so the bar is not offered on one. */}
                  {!failed && (
                    <div className="max-w-[min(34rem,85%)]">
                      <ReactionBar
                        reactions={message.reactions ?? []}
                        myAgentId={myAgentId}
                        outbound={outbound}
                        disabled={isReacting || myAgentId === null}
                        onPick={(emoji) => onReact(message.id, emoji)}
                        onClear={() => onReact(message.id, null)}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div ref={bottom} />
      </div>

      {/*
        Sticky to the scroller's own bottom edge, not fixed to the viewport: on a
        360px phone the composer already owns the bottom of the screen, and a pill
        floating over the send button is worse than no pill.
      */}
      {held > 0 && (
        <div className="pointer-events-none relative z-10 flex justify-center">
          <button
            type="button"
            onClick={jumpToBottom}
            className="tap pointer-events-auto absolute -top-12 rounded-full border border-(--color-line) bg-(--color-brand) px-3.5 py-1.5 text-small font-medium text-(--color-brand-ink) shadow-lg"
          >
            {held === 1 ? '1 new message' : `${held} new messages`} ↓
          </button>
        </div>
      )}

      <footer className="shrink-0 border-t border-(--color-line) bg-(--color-surface) px-3 py-2.5">
        {sendError && (
          <p role="alert" className="mb-2 rounded border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
            {sendError}
          </p>
        )}

        {/* Resolved beats everything else the footer could say. A composer under a
            conversation somebody has deliberately closed invites a reply that
            silently reopens it, and the reopen is what the resolution reports are
            counted from. The bar states the state and offers the one action. */}
        {resolved ? (
          <div className="flex items-center justify-between gap-3 py-0.5">
            <p className="text-body text-(--color-muted)">Conversation closed</p>
            <button
              type="button"
              onClick={onReopen}
              disabled={isSaving}
              className="tap flex h-9 shrink-0 items-center gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) px-3 text-small font-medium text-(--color-ink) transition-colors hover:bg-(--color-sunken) disabled:opacity-60"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="m5 12.5 4.5 4.5L19 7.5" />
              </svg>
              Tap to open again
            </button>
          </div>
        ) : (
        <>
        {/* A note is written in a different mode with a different colour and a
            different verb, because the failure it guards against is not a broken
            page — it is telling a customer something written about them. */}
        <div className="mb-2 flex gap-1">
          {(['reply', 'note'] as const).map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              className={`tap rounded px-3 py-1 text-small font-medium transition-colors ${
                mode === value
                  ? value === 'note'
                    ? 'bg-(--color-note) text-(--color-note-ink)'
                    : 'bg-(--color-sunken) text-(--color-ink)'
                  : 'text-(--color-muted) hover:text-(--color-ink)'
              }`}
            >
              {value === 'reply' ? 'Reply' : 'Internal note'}
            </button>
          ))}
        </div>

        {mode === 'note' ? (
          <NoteComposer agents={agents} isSaving={isSavingNote} onSave={onAddNote} />
        ) : open ? (
          <Composer
            contactName={conversation.displayName}
            contactId={conversation.contactId}
            isSending={isSending}
            isUploading={isUploading}
            onSend={onSend}
            onSendMedia={onSendMedia}
            onSendStoredMedia={onSendStoredMedia}
            onOpenTemplates={onOpenTemplates}
            onOpenProducts={onOpenProducts}
            onSchedule={onSchedule}
            onCancelScheduled={onCancelScheduled}
            scheduled={scheduled}
            windowExpiresAt={conversation.windowExpiresAt}
          />
        ) : (
          <>
          {/* The composer is gone, so this is the only place a message already
              scheduled can still be seen or called back. */}
          {scheduled.length > 0 && (
            <SchedulePanel
              listOnly
              windowExpiresAt={conversation.windowExpiresAt}
              pending={scheduled}
              isBusy={isSending}
              onSchedule={() => {}}
              onCancel={onCancelScheduled}
              onClose={() => {}}
            />
          )}
          {/* Not a wall with a button on it. Outside the window the honest control
              was a sentence about Meta's rules and a template picker, and that is
              where an agent stops: they wanted to answer somebody, and instead
              they are being asked to choose a marketing artefact. This renders the
              one nominated template as though it were the composer — fixed opening
              line, a gap to type into, fixed closing line — and falls back to the
              picker, with a reason, when there is nothing to render. */}
          <SessionOpener
            isSending={isSending}
            onSendTemplate={onSendTemplate}
            onOpenTemplates={onOpenTemplates}
          />
          </>
        )}
        </>
        )}
      </footer>

    </section>
  );
}

/**
 * A basket, laid out as the receipt it is: each line with quantity and unit
 * price, the total, and the note the customer typed with it. Prices are what
 * the catalogue said at the moment of ordering.
 */
function OrderCard({ order }: { order: OrderPayload }) {
  const units = order.items.reduce((n, i) => n + i.quantity, 0);
  return (
    <div className="mb-1 min-w-56 rounded-(--radius-field) border border-(--color-line) bg-(--color-surface) text-(--color-ink)">
      <p className="flex items-center gap-1.5 border-b border-(--color-line) px-2.5 py-1.5 text-small font-semibold">
        <span aria-hidden="true">🛒</span> Order · {units} {units === 1 ? 'item' : 'items'}
      </p>
      <ul className="divide-y divide-(--color-line-dim)">
        {order.items.map((item, index) => (
          <li key={`${item.retailerId ?? 'item'}-${index}`} className="flex items-center gap-2 px-2.5 py-1.5">
            {item.imageUrl
              ? <img src={item.imageUrl} alt="" className="size-9 shrink-0 rounded object-cover" loading="lazy" />
              : <span className="grid size-9 shrink-0 place-items-center rounded bg-(--color-sunken) text-(--color-muted)" aria-hidden="true">🛍️</span>}
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body">{item.name ?? item.retailerId ?? 'Product'}</span>
              <span className="block text-micro text-(--color-muted)">{item.quantity} × {formatAmount(item.price, item.currency)}</span>
            </span>
            <span className="shrink-0 text-small tabular-nums">{formatAmount(item.price * item.quantity, null)}</span>
          </li>
        ))}
      </ul>
      <p className="flex items-center justify-between border-t border-(--color-line) px-2.5 py-1.5 text-small font-semibold tabular-nums">
        <span>Total</span><span>{formatAmount(order.total, order.currency)}</span>
      </p>
      {order.text && <p className="border-t border-(--color-line-dim) px-2.5 py-1.5 text-small whitespace-pre-wrap">{order.text}</p>}
    </div>
  );
}

/** Two entries on the same IST day — which is the only day a reader here means. */
function sameDay(a: string, b: string): boolean {
  return istDayKey(a) === istDayKey(b);
}

function formatDay(iso: string): string {
  const today = istDayKey(new Date().toISOString());
  const yesterday = istDayKey(new Date(Date.now() - 86_400_000).toISOString());
  const day = istDayKey(iso);

  if (day === today) return 'Today';
  if (day === yesterday) return 'Yesterday';
  return formatIstLongDate(iso);
}

/**
 * The centred date separator.
 *
 * Extracted because three branches of the thread now need it — a message, a note
 * and an event can each be the first thing on a day — and three copies of it is
 * three places for the wording to drift.
 */
function DayPill({ at }: { at: string }) {
  return (
    <div className="my-3 flex justify-center">
      <span className="rounded-(--radius-chip) bg-(--color-surface) px-3 py-1 text-micro font-medium text-(--color-muted) shadow-sm">
        {formatDay(at)}
      </span>
    </div>
  );
}

/** When an entry happened, whatever kind it is. */
function stampOf(entry: ThreadEntry): string {
  switch (entry.kind) {
    case 'message': return entry.message.createdAt;
    case 'note': return entry.note.createdAt;
    case 'event': return entry.event.createdAt;
  }
}
